import React, {useState, useEffect} from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import {Refresh, Search, Public} from '@mui/icons-material';
import {useCountry} from 'context/CountryContext';
import {countryApi} from 'api/countryApi';

const CountryDropdown = ({
  value,
  onChange,
  label = 'Country',
  required = false,
  error,
  helperText,
  showActiveOnly = true,
  fullWidth = true,
  size = 'medium',
  variant = 'outlined',
  disabled = false,
  autoSelectDefault = true,
  showSearch = true,
  showRegionFilter = false,
  includeFlag = true,
  showAllOption = false,
}) => {
  const {
    countries,
    loading,
    error: countryError,
    refreshCountries,
    getDefaultCountry,
  } = useCountry();

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');

  useEffect(() => {
    if (countries.length > 0) {
      let filtered = [...countries];

      // Apply search filter
      if (searchTerm) {
        filtered = filtered.filter(
          country =>
            country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            country.countryCode
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            country.region?.toLowerCase().includes(searchTerm.toLowerCase()),
        );
      }

      // Apply region filter
      if (selectedRegion && showRegionFilter) {
        filtered = filtered.filter(
          country => country.region === selectedRegion,
        );
      }

      setFilteredCountries(filtered);
    } else {
      setFilteredCountries([]);
    }
  }, [countries, searchTerm, selectedRegion]);

  useEffect(() => {
    // Auto-select default country if no value selected
    if (autoSelectDefault && !value && countries.length > 0) {
      const defaultCountry = getDefaultCountry();
      if (defaultCountry) {
        onChange && onChange(defaultCountry.id);
      }
    }
  }, [countries, value, autoSelectDefault]);

  const handleRefresh = async () => {
    await refreshCountries();
    setSearchTerm('');
    setSelectedRegion('');
  };

  const handleChange = e => {
    const selectedValue = e.target.value;
    onChange && onChange(selectedValue);
  };

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
  };

  const getRegions = () => {
    const regions = new Set();
    countries.forEach(country => {
      if (country.region) {
        regions.add(country.region);
      }
    });
    return Array.from(regions).sort();
  };

  if (loading && countries.length === 0) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading countries...</Typography>
      </Box>
    );
  }

  const errorToShow = countryError || error;

  const getSelectedCountry = () => {
    return countries.find(c => c.id === value);
  };

  const selectedCountry = getSelectedCountry();

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <FormControl
          fullWidth={fullWidth}
          size={size}
          error={!!errorToShow}
          disabled={disabled || countries.length === 0}
          variant={variant}>
          <InputLabel>
            {label}
            {required && <span style={{color: 'red'}}>*</span>}
          </InputLabel>

          <Select
            value={value || ''}
            onChange={handleChange}
            label={label}
            displayEmpty
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 400,
                },
              },
            }}
            renderValue={selected => {
              if (!selected) {
                if (showAllOption) {
                  return (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        fontSize={size === 'small' ? '0.875rem' : '1rem'}>
                        🌍
                      </Typography>
                      <Typography>All Countries</Typography>
                    </Box>
                  );
                }
                return '';
              }
              if (!selectedCountry) return '';

              return (
                <Box display="flex" alignItems="center" gap={1}>
                  {includeFlag && selectedCountry.countryCode && (
                    <Typography
                      fontSize={size === 'small' ? '0.875rem' : '1rem'}>
                      {countryApi.getCountryFlagEmoji(
                        selectedCountry.countryCode,
                      )}
                    </Typography>
                  )}
                  <Typography>
                    {countryApi.formatCountryLabel(selectedCountry)}
                  </Typography>
                  {selectedCountry.isDefault && (
                    <Chip
                      label="Default"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              );
            }}>
            {/* Search Bar */}
            {/* {showSearch && (
              <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search countries..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )} */}

            {/* Region Filter */}
            {showRegionFilter && getRegions().length > 0 && (
              <Box sx={{p: 1, borderBottom: '1px solid #e0e0e0'}}>
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedRegion}
                    onChange={e => setSelectedRegion(e.target.value)}
                    displayEmpty>
                    <MenuItem value="">All Regions</MenuItem>
                    {getRegions().map(region => (
                      <MenuItem key={region} value={region}>
                        {region}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* All Countries Option */}
            {showAllOption && (
              <MenuItem value="">
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontSize="1.125rem">🌍</Typography>
                  <Box>
                    <Typography variant="body1">All Countries</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Apply to all countries
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            )}

            {/* Country List */}
            {filteredCountries.length === 0 ? (
              <MenuItem disabled>
                <Typography color="textSecondary" align="center" width="100%">
                  No countries found
                </Typography>
              </MenuItem>
            ) : (
              filteredCountries.map(country => (
                <MenuItem key={country.id} value={country.id}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    width="100%"
                    alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      {includeFlag && country.countryCode && (
                        <Typography fontSize="1.125rem">
                          {countryApi.getCountryFlagEmoji(country.countryCode)}
                        </Typography>
                      )}

                      <Box>
                        <Typography variant="body1">{country.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {country.countryCode}{' '}
                          {country.region && `• ${country.region}`}
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                      {country.isDefault && (
                        <Chip
                          label="Default"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}

                      {country.currencyCode && (
                        <Tooltip title={`Currency: ${country.currencyCode}`}>
                          <Chip
                            label={country.currencyCode}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <Tooltip title="Refresh countries">
          <IconButton size="small" onClick={handleRefresh} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {helperText && !errorToShow && (
        <Typography variant="caption" color="textSecondary" sx={{mt: 0.5}}>
          {helperText}
        </Typography>
      )}

      {errorToShow && (
        <Alert severity="error" size="small" sx={{mt: 1}}>
          {errorToShow}
        </Alert>
      )}

      {/* Selected Country Details */}
      {selectedCountry && selectedCountry.description && (
        <Alert
          severity="info"
          size="small"
          sx={{mt: 1}}
          icon={<Public fontSize="small" />}>
          <Typography variant="caption">
            {selectedCountry.description}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default CountryDropdown;
